import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { APP_CONFIG } from '../config/environment'
import { AuthService } from './auth.service'
import { SyncAuditEntries } from '@/domain/sync-audit'

export type AtomicDeliveryStatus = 'QUARANTINED' | 'PENDING' | 'IN_FLIGHT' | 'ACKNOWLEDGED'

export interface AtomicDelivery {
  deliveryId: string
  merchantId: string
  entities: AtomicDeliveryEntity[]
  requestPayload?: unknown
  status: AtomicDeliveryStatus
  attemptCount: number
  manualRetryCount: number
  lastAttemptAt?: string | null
  quarantinedAt?: string | null
  lastError?: string | null
}

export interface AtomicDeliveryEntity { type: string; id: string }

interface RequestOptions {
  signal?: AbortSignal
}

interface RequestContext {
  controller: AbortController
  signal: AbortSignal
}

@Injectable({
  providedIn: 'root',
})
export class SyncAuditService implements OnDestroy {
  private readonly postgrestBaseUrl = APP_CONFIG.postgrestBaseUrl
  private readonly requestControllers = new Map<string, AbortController>()
  private readonly quarantineEntityCountSubject = new BehaviorSubject<number | null>(null)
  readonly quarantineEntityCount$ = this.quarantineEntityCountSubject.asObservable()

  constructor(private readonly authService: AuthService) {}

  ngOnDestroy(): void {
    this.cancelAllRequests()
  }

  cancelAllRequests(): void {
    Array.from(this.requestControllers.values()).forEach((controller) =>
      controller.abort(),
    )
    this.requestControllers.clear()
  }

  setQuarantineEntityCount(count: number): void {
    this.quarantineEntityCountSubject.next(count)
  }

  private startRequest(key: string): AbortController {
    const existing = this.requestControllers.get(key)
    if (existing) {
      existing.abort()
    }

    const controller = new AbortController()
    this.requestControllers.set(key, controller)
    return controller
  }

  private clearRequest(key: string, controller: AbortController): void {
    if (this.requestControllers.get(key) === controller) {
      this.requestControllers.delete(key)
    }
  }

  private createRequestContext(
    key: string,
    externalSignal?: AbortSignal,
  ): RequestContext {
    const controller = this.startRequest(key)

    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort(externalSignal.reason)
      } else {
        const abort = () => controller.abort(externalSignal.reason)
        externalSignal.addEventListener('abort', abort, { once: true })
        controller.signal.addEventListener(
          'abort',
          () => externalSignal.removeEventListener('abort', abort),
          { once: true },
        )
      }
    }

    return { controller, signal: controller.signal }
  }

  async loadSyncAuditEntries(
    ids: string[],
    options?: RequestOptions,
  ): Promise<SyncAuditEntries> {
    const normalizedIds = Array.from(
      new Set(ids.map((id) => id.trim()).filter((id) => id.length > 0)),
    )

    if (normalizedIds.length === 0) {
      return []
    }

    const { controller, signal } = this.createRequestContext(
      'loadSyncAuditEntries',
      options?.signal,
    )

    try {
      const entries = await Promise.all(
        normalizedIds.map((id) => this.fetchSyncAuditEntry(id, signal)),
      )

      return entries.filter(
        (entry): entry is NonNullable<typeof entry> => entry !== null,
      )
    } finally {
      this.clearRequest('loadSyncAuditEntries', controller)
    }
  }

  async loadSalesforceQuarantines(options?: RequestOptions): Promise<SyncAuditEntries> {
    const { controller, signal } = this.createRequestContext(
      'loadSalesforceQuarantines',
      options?.signal,
    )

    try {
      const token = await this.authService.getPostgrestJwtToken(signal)
      const query = new URLSearchParams({
        select: 'id,friendly_name,amount,status,details,request_payload',
        id: 'like.salesforce_atomic_delivery#*',
        status: 'eq.QUARANTINED',
        order: 'updated_at.desc',
      })
      const response = await fetch(`${this.postgrestBaseUrl}/sync_audit?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      })
      if (!response.ok) {
        const failure = await response.text()
        // Older deployments may still be applying the additive quarantine
        // columns. The real-time proxy remains usable during that window.
        if (response.status === 400 && failure.includes('column sync_audit.') && failure.includes('does not exist')) {
          const fallbackQuery = new URLSearchParams({
            select: 'id,friendly_name,amount',
            id: 'like.salesforce_atomic_delivery#*',
          })
          const fallback = await fetch(`${this.postgrestBaseUrl}/sync_audit?${fallbackQuery}`, {
            headers: { Authorization: `Bearer ${token}` },
            signal,
          })
          if (fallback.ok) return (await fallback.json()) as SyncAuditEntries
        }
        throw new Error(`Failed to load Salesforce quarantines: ${response.status} ${response.statusText}`)
      }
      return (await response.json()) as SyncAuditEntries
    } finally {
      this.clearRequest('loadSalesforceQuarantines', controller)
    }
  }

  async listAtomicDeliveries(options?: RequestOptions): Promise<AtomicDelivery[]> {
    const response = await this.proxyRequest('/atomic-deliveries', { signal: options?.signal })
    const payload = await this.readProxyPayload(response)
    if (!response.ok) throw new Error(this.proxyError(payload, 'Failed to load current quarantines'))
    const items = payload && typeof payload === 'object' && Array.isArray((payload as { items?: unknown }).items)
      ? (payload as { items: unknown[] }).items
      : []
    return items.map((item) => this.toAtomicDelivery(item)).filter((item): item is AtomicDelivery => item !== null)
  }

  async getAtomicDelivery(merchantId: string, deliveryId: string, options?: RequestOptions): Promise<AtomicDelivery> {
    const query = new URLSearchParams({ merchantId, deliveryId })
    const response = await this.proxyRequest(`/atomic-deliveries?${query}`, { signal: options?.signal })
    const payload = await this.readProxyPayload(response)
    if (!response.ok) throw new Error(this.proxyError(payload, 'Failed to load delivery status'))
    const item = this.toAtomicDelivery(payload)
    if (!item) throw new Error('Delivery status response was invalid')
    return item
  }

  async retryAtomicDelivery(merchantId: string, deliveryId: string, options?: RequestOptions): Promise<AtomicDelivery> {
    const response = await this.proxyRequest('/atomic-deliveries/retry', {
      method: 'POST', signal: options?.signal, body: JSON.stringify({ merchantId, deliveryId }),
    })
    const payload = await this.readProxyPayload(response)
    if (response.status === 409) throw new Error('This delivery is no longer quarantined and cannot be retried.')
    if (!response.ok) throw new Error(this.proxyError(payload, 'Failed to request retry'))
    const item = this.toAtomicDelivery(payload && typeof payload === 'object' ? (payload as { item?: unknown }).item : null)
    if (!item) throw new Error('Retry response was invalid')
    return item
  }

  async deleteAtomicDelivery(merchantId: string, deliveryId: string, options?: RequestOptions): Promise<void> {
    const query = new URLSearchParams({ merchantId, deliveryId })
    const response = await this.proxyRequest(`/atomic-deliveries?${query}`, {
      method: 'DELETE', signal: options?.signal,
    })
    const payload = await this.readProxyPayload(response)
    if (response.status === 409) throw new AtomicDeliveryConflictError('This delivery is no longer quarantined.')
    if (!response.ok) throw new Error(this.proxyError(payload, 'Failed to permanently delete delivery'))
  }

  private async proxyRequest(path: string, options: RequestInit): Promise<Response> {
    const token = await this.authService.getAzureAccessToken()
    return fetch(`${APP_CONFIG.lambdaAuthUrl.replace(/\/$/, '')}${path}`, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
  }

  private async readProxyPayload(response: Response): Promise<unknown> {
    const text = await response.text()
    if (!text) return null
    try { return JSON.parse(text) } catch { return null }
  }

  private proxyError(payload: unknown, fallback: string): string {
    return payload && typeof payload === 'object' && typeof (payload as { error?: unknown }).error === 'string'
      ? (payload as { error: string }).error
      : fallback
  }

  private toAtomicDelivery(value: unknown): AtomicDelivery | null {
    if (!value || typeof value !== 'object') return null
    const item = value as Record<string, unknown>
    if (typeof item.deliveryId !== 'string' || typeof item.merchantId !== 'string' || typeof item.status !== 'string') return null
    return {
      deliveryId: item.deliveryId, merchantId: item.merchantId,
      entities: Array.isArray(item.entities)
        ? item.entities.filter((entity): entity is Record<string, unknown> => !!entity && typeof entity === 'object')
          .map((entity) => ({ type: typeof entity.type === 'string' ? entity.type : 'unknown', id: typeof entity.id === 'string' ? entity.id : '' }))
          .filter((entity) => entity.id.length > 0)
        : [],
      requestPayload: item.requestPayload ?? item.request_payload,
      status: item.status as AtomicDeliveryStatus,
      attemptCount: Number(item.attemptCount) || 0, manualRetryCount: Number(item.manualRetryCount) || 0,
      lastAttemptAt: typeof item.lastAttemptAt === 'string' ? item.lastAttemptAt : null,
      quarantinedAt: typeof item.quarantinedAt === 'string' ? item.quarantinedAt : null,
      lastError: typeof item.lastError === 'string' ? item.lastError : null,
    }
  }

  private async fetchSyncAuditEntry(
    id: string,
    signal: AbortSignal,
  ): Promise<SyncAuditEntries[number] | null> {
    const token = await this.authService.getPostgrestJwtToken(signal)
    const response = await fetch(
      `${this.postgrestBaseUrl}/sync_audit?id=eq.${encodeURIComponent(id)}&select=id,friendly_name,amount`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal,
      },
    )

    if (!response.ok) {
      throw new Error(
        `Failed to load sync audit entry ${id}: ${response.status} ${response.statusText}`,
      )
    }

    const rows = (await response.json()) as SyncAuditEntries
    return rows[0] ?? null
  }
}

export class AtomicDeliveryConflictError extends Error {}
